import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Headset, Bot, Loader2 } from "lucide-react";
import { getConsent, CONSENT_EVENT } from "../lib/consent";
import ChatMessages from "./ChatMessages";

const API = process.env.REACT_APP_BACKEND_URL;
const CID_KEY = "ct_chat_cid";
const QUICK = ["Vad kostar Startpaket?", "Öppettider", "Hur bokar jag halkbana?", "Behöver jag körkortstillstånd?", "Vilka språk finns teorin på?"];
const POLL_MS = 3000;

async function readStream(res, onDelta) {
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const line = p.split("\n").find((l) => l.startsWith("data: "));
      if (line && !p.startsWith("event: done")) onDelta(JSON.parse(line.slice(6)));
    }
  }
}

export default function ChatWidget() {
  const [consentDecided, setConsentDecided] = useState(() => getConsent().decided);
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(false);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState("");
  const [form, setForm] = useState({ name: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const streamingRef = useRef(false);

  useEffect(() => {
    const h = (e) => setConsentDecided(e.detail.decided);
    window.addEventListener(CONSENT_EVENT, h);
    return () => window.removeEventListener(CONSENT_EVENT, h);
  }, []);

  useEffect(() => {
    if (!open || window.innerWidth >= 640) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    fetch(`${API}/api/chat/status`).then((r) => r.json()).then((d) => setOnline(d.online)).catch(() => {});
  }, [open]);

  useEffect(() => {
    const cid = localStorage.getItem(CID_KEY);
    if (!cid || !open) return;
    fetch(`${API}/api/chat/${cid}`).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!d) return localStorage.removeItem(CID_KEY);
      setConv(d.conversation); setMessages(d.messages); setOnline(d.online);
    }).catch(() => {});
  }, [open]);

  const askAi = useCallback(async (cid) => {
    if (streamingRef.current) return;
    streamingRef.current = true;
    setStreaming(" ");
    let text = "";
    try {
      const res = await fetch(`${API}/api/chat/${cid}/ai-reply`, { method: "POST" });
      await readStream(res, (d) => { text += d; setStreaming(text); });
    } finally {
      streamingRef.current = false;
      setStreaming("");
      const d = await fetch(`${API}/api/chat/${cid}`).then((r) => r.json()).catch(() => null);
      if (d) { setConv(d.conversation); setMessages(d.messages); }
    }
  }, []);

  useEffect(() => {
    if (!open || !conv) return;
    const t = setInterval(async () => {
      if (streamingRef.current) return;
      const last = messages[messages.length - 1];
      const after = last ? last.created_at : "";
      const d = await fetch(`${API}/api/chat/${conv.id}?after=${encodeURIComponent(after)}`).then((r) => r.json()).catch(() => null);
      if (!d) return;
      setConv(d.conversation); setOnline(d.online);
      if (d.messages.length) setMessages((m) => [...m, ...d.messages.filter((x) => !m.some((y) => y.id === x.id))]);
      const all = [...messages, ...d.messages];
      const tail = all[all.length - 1];
      if (d.conversation.mode === "ai" && d.conversation.status === "open" && tail?.role === "visitor") askAi(conv.id);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [open, conv, messages, askAi]);

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await fetch(`${API}/api/chat/start`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) }).then((r) => r.json());
      localStorage.setItem(CID_KEY, d.conversation.id);
      setConv(d.conversation); setMessages(d.messages);
    } finally { setBusy(false); }
  };

  const sendText = async (text) => {
    if (!text || !conv || streamingRef.current) return;
    setDraft("");
    const d = await fetch(`${API}/api/chat/${conv.id}/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) }).then((r) => r.json());
    if (d.message) setMessages((m) => [...m, d.message]);
    if (d.mode === "ai") askAi(conv.id);
  };
  const send = (e) => { e.preventDefault(); sendText(draft.trim()); };

  const reset = () => { localStorage.removeItem(CID_KEY); setConv(null); setMessages([]); };
  const [confirmEnd, setConfirmEnd] = useState(false);
  const endChat = async () => {
    if (!conv) return;
    setConfirmEnd(false);
    await fetch(`${API}/api/chat/${conv.id}/close`, { method: "POST" });
    const d = await fetch(`${API}/api/chat/${conv.id}`).then((r) => r.json()).catch(() => null);
    if (d) { setConv(d.conversation); setMessages(d.messages); }
  };

  if (!consentDecided) return null;

  const humanMode = conv ? conv.mode === "human" : online;
  const showQuick = conv && conv.status === "open" && !messages.some((m) => m.role === "visitor");
  const waiting = conv && humanMode && messages[messages.length - 1]?.role === "visitor";

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-launcher" aria-label="Öppna chatt" className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-[90] group inline-flex items-center gap-2.5 pl-4 pr-5 py-3 rounded-full bg-[#0B2545] text-white shadow-2xl shadow-[#0B2545]/40 hover:bg-[#13315C] transition-colors chat-pop">
          <span className="relative"><MessageCircle size={22} /><span data-testid="chat-launcher-dot" className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-[#0B2545] ${online ? "bg-emerald-400" : "bg-[#ffce3a]"}`} /></span>
          <span className="text-sm font-bold">{online ? "Chatta med receptionen" : "Fråga oss"}</span>
        </button>
      )}

      {open && (
        <section data-testid="chat-panel" className="fixed z-[120] inset-0 sm:inset-auto sm:bottom-5 sm:right-5 w-full sm:w-[380px] h-full sm:h-[600px] sm:max-h-[calc(100vh-2.5rem)] flex flex-col bg-white sm:rounded-3xl shadow-2xl shadow-slate-900/30 sm:border border-slate-200 overflow-hidden chat-pop" role="dialog" aria-label="Livechatt">
          <header className="relative bg-[#0B2545] text-white px-4 sm:px-5 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3 sm:py-4 flex items-center gap-3 overflow-hidden">
            <div className="absolute -top-10 -right-6 w-32 h-32 rounded-full bg-[#ffce3a]/15 blur-2xl pointer-events-none" />
            <span className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${humanMode ? "bg-emerald-400/20 text-emerald-300" : "bg-[#ffce3a] text-[#0B2545]"}`}>{humanMode ? <Headset size={18} /> : <Bot size={18} />}</span>
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-[15px] leading-tight">{humanMode ? "Receptionen" : "City-assistenten"}</p>
              <p className="text-[11px] text-slate-300 flex items-center gap-1.5 truncate" data-testid="chat-status-text"><span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${humanMode ? "bg-emerald-400 animate-pulse" : "bg-[#ffce3a]"}`} />{humanMode ? "Online – svarar personligen" : online ? "Receptionen är online" : "AI svarar dygnet runt"}</p>
            </div>
            {conv && conv.status === "open" && (
              <button onClick={() => setConfirmEnd(true)} data-testid="chat-end-btn" className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-slate-200 transition-colors">Avsluta</button>
            )}
            <button onClick={() => setOpen(false)} aria-label="Stäng chatt" data-testid="chat-close" className="text-slate-300 hover:text-white p-1.5 -mr-1.5 rounded-lg hover:bg-white/10"><X size={20} /></button>
          </header>

          {!conv ? (
            <form onSubmit={start} className="flex-1 flex flex-col justify-center px-5 sm:px-6 py-6 gap-3.5 bg-[#f6f9fc] overflow-y-auto" data-testid="chat-start-form">
              <div className="flex gap-1.5 mb-1" aria-hidden="true"><span className="w-2.5 h-2.5 rounded-full bg-rose-400" /><span className="w-2.5 h-2.5 rounded-full bg-[#ffce3a]" /><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /></div>
              <div>
                <h3 className="font-display text-lg sm:text-xl font-bold text-[#0B2545] leading-snug">Hej! Vad kan vi hjälpa dig med?</h3>
                <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">{online ? "Receptionen är online och svarar direkt i chatten." : "Receptionen är inte online just nu – vår AI-assistent svarar på dina frågor direkt."} Fyll i dina uppgifter så vi kan återkomma om chatten avslutas.</p>
              </div>
              <input data-testid="chat-name-input" required placeholder="Ditt namn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 text-base sm:text-[15px] rounded-xl border border-slate-200 focus:border-[#1B6CA8] focus:ring-2 focus:ring-[#1B6CA8]/20 outline-none" />
              <input data-testid="chat-contact-input" required placeholder="E-post eller telefon" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="w-full px-4 py-3 text-base sm:text-[15px] rounded-xl border border-slate-200 focus:border-[#1B6CA8] focus:ring-2 focus:ring-[#1B6CA8]/20 outline-none" />
              <button data-testid="chat-start-btn" disabled={busy} className="w-full py-3 rounded-xl bg-[#ffce3a] text-[#0B2545] font-bold hover:bg-[#ffd85f] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy && <Loader2 size={16} className="animate-spin" />} Starta chatten</button>
              <p className="text-[11px] text-slate-400 text-center leading-snug">Uppgifterna hanteras enligt vår <a href="/integritetspolicy" className="underline">integritetspolicy</a>.</p>
            </form>
          ) : (
            <>
              <ChatMessages messages={messages} streaming={streaming} waiting={waiting} viewer="visitor" />
              {confirmEnd && (
                <div className="px-4 py-3 bg-[#0B2545] text-white flex items-center justify-between gap-3" data-testid="chat-end-confirm">
                  <span className="text-[13px] font-semibold">Vill du avsluta chatten?</span>
                  <span className="flex gap-2">
                    <button onClick={() => setConfirmEnd(false)} data-testid="chat-end-cancel" className="px-3 py-1.5 rounded-lg text-[13px] font-semibold text-slate-200 hover:bg-white/10">Avbryt</button>
                    <button onClick={endChat} data-testid="chat-end-confirm-btn" className="px-3 py-1.5 rounded-lg bg-[#ffce3a] text-[#0B2545] text-[13px] font-bold hover:bg-[#ffd85f]">Avsluta</button>
                  </span>
                </div>
              )}
              {showQuick && (
                <div className="px-3 pb-2 bg-[#f6f9fc] flex gap-2 overflow-x-auto no-scrollbar" data-testid="chat-quick-questions">
                  {QUICK.map((q) => (
                    <button key={q} type="button" onClick={() => sendText(q)} data-testid="chat-quick-btn" className="flex-shrink-0 px-3.5 py-2 rounded-full bg-white border border-[#0B2545]/15 text-[#0B2545] text-[13px] font-semibold hover:bg-[#ffce3a] hover:border-[#ffce3a] transition-colors whitespace-nowrap">{q}</button>
                  ))}
                </div>
              )}
              {conv.status === "closed" ? (
                <div className="px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-slate-100 text-center text-[13px] text-slate-500" data-testid="chat-closed-note">Chatten är avslutad. <button onClick={reset} className="font-bold text-[#1B6CA8] hover:underline" data-testid="chat-new-btn">Starta ny chatt</button></div>
              ) : (
                <form onSubmit={send} className="px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] border-t border-slate-100 flex items-center gap-2 bg-white">
                  <input data-testid="chat-message-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Skriv ditt meddelande…" className="flex-1 min-w-0 px-4 py-2.5 rounded-full bg-[#f6f9fc] border border-slate-200 focus:border-[#1B6CA8] outline-none text-base sm:text-[15px]" />
                  <button data-testid="chat-send-btn" disabled={!draft.trim() || !!streaming} aria-label="Skicka" className="w-10 h-10 flex-shrink-0 rounded-full bg-[#ffce3a] text-[#0B2545] flex items-center justify-center hover:bg-[#ffd85f] disabled:opacity-40 transition-colors"><Send size={16} /></button>
                </form>
              )}
            </>
          )}
        </section>
      )}
    </>
  );
}
